import { FC, useState } from 'react'
import ShapefileUploader from 'src/components/shape-file-uploader'
import shp from 'shpjs'
import { Button } from 'src/components/button'
import { Spinner } from 'src/components/spinner'
import { Colors } from 'src/components/theme'
import styled from '@emotion/styled'
import { FlexColumn } from 'src/components/flex-containers'
import { growl } from 'src/components/growl'
import { UserListItem } from 'src/common-services'
import { userListsClient } from 'src/clients'
import * as atoms from 'src/atoms/user-lists/user-lists-modal'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { useSetUpUserLists } from 'src/hooks/use-setup-userlists'

const ConfirmButton = styled(Button)`
  background-color: ${Colors.blueDark};
  align-self: center;
  width: 100px;
`

export const ShapefileUploadContent: FC = () => {
  const [geojson, setGeojson] = useState<shp.FeatureCollectionWithFilename | null>(null)
  const [loading, setLoading] = useState(false)
  const selectedListId = useRecoilValue(atoms.selectedListId)
  const setUploadSection = useSetRecoilState(atoms.uploadSection)
  const { setUpUserLists } = useSetUpUserLists()
  const setSuccesssMessage = useSetRecoilState(atoms.saveItemSuccessMessage)

  const handleUpload = (result: shp.FeatureCollectionWithFilename | shp.FeatureCollectionWithFilename[] | null) => {
    if (!result) {
      setGeojson(null)
      return
    }

    if (Array.isArray(result)) {
      growl({
        text: 'Multi collections not supported',
        severity: 'error',
      })
      return
    }

    if ((result as shp.FeatureCollectionWithFilename).features.length !== 1) {
      growl({
        text: 'Multi features not supported',
        severity: 'error',
      })
      return
    }

    const feature = result.features[0]
    if (
      feature.geometry.type === 'Point' ||
      feature.geometry.type === 'MultiPoint' ||
      feature.geometry.type === 'LineString' ||
      feature.geometry.type === 'MultiLineString' ||
      feature.geometry.type === 'GeometryCollection'
    ) {
      growl({
        text: 'Only Polygon/MultiPolygon geometry type is supported',
        severity: 'error',
      })
      return
    }

    setGeojson(result as shp.FeatureCollectionWithFilename)
  }

  const handleSave = async () => {
    if (!geojson || !selectedListId || loading) {
      return
    }

    const feature = geojson.features[0]

    if (feature.geometry.type !== 'Polygon' && feature.geometry.type !== 'MultiPolygon') {
      growl({
        text: 'Only Polygon/MultiPolygon geometry type is supported',
        severity: 'error',
      })
      return
    }

    const item: UserListItem = {
      type: 'custom-area',
      data: {
        type: feature.geometry.type,
        name: geojson.fileName ?? 'Shapefile Polygon',
        description: '',
        coordinates: feature.geometry.coordinates as any,
      },
    }

    try {
      setLoading(true)
      await userListsClient.addListItem(selectedListId, item)
      await setUpUserLists()
      setSuccesssMessage('Shapefile polygon added to list')
      setUploadSection(undefined)
    } catch {
      growl({
        text: 'Error adding item to list',
        severity: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  if (!selectedListId) {
    return null
  }

  return (
    <FlexColumn gap={16}>
      <ShapefileUploader geojson={geojson} onUpload={handleUpload} />
      {geojson && (
        <ConfirmButton
          variant="contained"
          onClick={handleSave}
          disabled={loading}
          startIcon={loading ? <Spinner size={14} /> : undefined}>
          Save
        </ConfirmButton>
      )}
    </FlexColumn>
  )
}
